/**
 * Bildbibliothek für den Editor.
 *
 * Zwei Wege, ein Bild in den Newsletter zu bekommen:
 *  - aus der CI-Bibliothek der Kanzlei (Logos, Leistungsfotos, Motive)
 *  - eigene Bilder, per Ziehen auf die Ablagefläche oder per Auswahl
 *
 * Beides landet als absolute https-Adresse in der Vorlage. Das ist keine
 * Bequemlichkeit, sondern Voraussetzung: im Postfach des Empfängers gibt es
 * keinen Serverkontext, und base64 bläht die Nachricht auf, bis Gmail sie
 * abschneidet.
 *
 * Ziehen und Ablegen funktioniert in beide Richtungen — Bilder aus der
 * Bibliothek lassen sich direkt in die Bearbeitungsfläche ziehen.
 */

import { api } from './api.js';
import { el, ersetzen, laedt, meldung } from './ui.js';
import { bildVorbereiten, bytesFormatieren } from './bild-verkleinern.js';

/** Zwischenspeicher, damit die Liste nicht bei jedem Reiterwechsel neu lädt. */
let zwischenspeicher = null;

/**
 * @returns {Promise<{bilder: any[]}>}
 */
export async function bilderLaden(neuLaden = false) {
  if (!zwischenspeicher || neuLaden) {
    zwischenspeicher = await api('/api/assets');
  }
  return zwischenspeicher;
}

/**
 * Lädt eine Datei hoch — nach Verkleinerung auf eine für E-Mail sinnvolle Größe.
 *
 * @param {File} datei
 * @returns {Promise<{adresse: string, ersparnis: string|null}>}
 */
export async function bildHochladen(datei) {
  const aufbereitet = await bildVorbereiten(datei);

  const inhaltBase64 = await new Promise((aufloesen, ablehnen) => {
    const leser = new FileReader();
    leser.onload = () => aufloesen(String(leser.result));
    leser.onerror = () => ablehnen(new Error('Die Datei konnte nicht gelesen werden.'));
    leser.readAsDataURL(aufbereitet.blob);
  });

  const ergebnis = await api('/api/assets', {
    methode: 'POST',
    koerper: { dateiname: aufbereitet.dateiname, inhaltBase64 },
  });
  zwischenspeicher = null;

  const ersparnis =
    aufbereitet.veraendert && aufbereitet.nachherBytes < aufbereitet.vorherBytes
      ? `Verkleinert von ${bytesFormatieren(aufbereitet.vorherBytes)} auf ` +
        `${bytesFormatieren(aufbereitet.nachherBytes)} (${aufbereitet.breite} px breit).`
      : null;

  return { ...ergebnis, ersparnis };
}

/**
 * Baut den Bildbereich für die linke Editorspalte.
 *
 * @param {object} optionen
 * @param {(adresse: string, beschriftung: string, naturbreite?: number) => void} optionen.beiAuswahl
 *   Wird bei Klick auf ein Bild aufgerufen (Alternative zum Ziehen).
 * @returns {HTMLElement}
 */
export function bildbereich({ beiAuswahl }) {
  const behaelter = el('div');
  const rasterBereich = el('div');
  const meldungsBereich = el('div');

  const dateiFeld = /** @type {HTMLInputElement} */ (
    el('input', { type: 'file', accept: 'image/png,image/jpeg,image/gif', style: 'display:none' })
  );

  const ablage = el('div.ablage', [
    el('div', { text: 'Bild hierher ziehen' }),
    el('div', { style: 'font-size:12px;margin-top:4px', text: 'oder klicken zum Auswählen' }),
  ]);

  /**
   * @param {File|undefined} datei
   */
  const hochladen = async (datei) => {
    if (!datei) return;
    ersetzen(meldungsBereich, laedt('Bild wird aufbereitet und hochgeladen …'));
    try {
      const { ersparnis } = await bildHochladen(datei);
      // Die Ersparnis nennen: sie erklärt, warum die Datei jetzt anders heißt
      // oder kleiner ist als das Original auf der Festplatte.
      ersetzen(meldungsBereich, ...(ersparnis ? [meldung('erfolg', ersparnis)] : []));
      await zeichnen(true);
    } catch (fehler) {
      ersetzen(
        meldungsBereich,
        meldung('fehler', fehler instanceof Error ? fehler.message : 'Hochladen fehlgeschlagen'),
      );
    }
  };

  ablage.addEventListener('click', () => dateiFeld.click());
  dateiFeld.addEventListener('change', () => hochladen(dateiFeld.files?.[0]));

  // Ziehen und Ablegen. dragover muss abgefangen werden, sonst öffnet der
  // Browser die Datei einfach in einem neuen Tab.
  for (const ereignis of ['dragenter', 'dragover']) {
    ablage.addEventListener(ereignis, (e) => {
      e.preventDefault();
      ablage.classList.add('bereit');
    });
  }
  for (const ereignis of ['dragleave', 'drop']) {
    ablage.addEventListener(ereignis, (e) => {
      e.preventDefault();
      ablage.classList.remove('bereit');
    });
  }
  ablage.addEventListener('drop', (e) => {
    const datei = /** @type {DragEvent} */ (e).dataTransfer?.files?.[0];
    hochladen(datei);
  });

  /**
   * @param {boolean} [neuLaden]
   */
  async function zeichnen(neuLaden = false) {
    ersetzen(rasterBereich, laedt('Bilder werden geladen …'));
    try {
      const { bilder } = await bilderLaden(neuLaden);

      /** @type {Map<string, any[]>} */
      const nachGruppe = new Map();
      for (const bild of bilder) {
        if (!nachGruppe.has(bild.gruppe)) nachGruppe.set(bild.gruppe, []);
        nachGruppe.get(bild.gruppe)?.push(bild);
      }

      const abschnitte = [...nachGruppe.entries()].map(([gruppe, eintraege]) =>
        el('details.vertiefung', { open: gruppe === 'Eigene Bilder' || gruppe === 'Logo' }, [
          el('summary', { text: `${gruppe} (${eintraege.length})` }),
          el('div', [
            el(
              'div.bild-raster',
              eintraege.map((bild) => kachel(bild, beiAuswahl)),
            ),
          ]),
        ]),
      );

      ersetzen(
        rasterBereich,
        ...(abschnitte.length
          ? abschnitte
          : [el('p.leer', { text: 'Noch keine Bilder vorhanden.' })]),
      );
    } catch (fehler) {
      ersetzen(
        rasterBereich,
        meldung('fehler', fehler instanceof Error ? fehler.message : 'Bilder nicht ladbar'),
      );
    }
  }

  behaelter.append(
    el('p.editor-hinweis', {
      text: 'Ziehen Sie ein Bild in die Nachricht oder klicken Sie es an, um es beim ausgewählten Bildbaustein einzusetzen.',
    }),
    ablage,
    dateiFeld,
    meldungsBereich,
    el('div', { style: 'height:14px' }),
    rasterBereich,
  );

  zeichnen();
  return behaelter;
}

/**
 * Eine einzelne Bildkachel — ziehbar in die Bearbeitungsfläche.
 *
 * @param {any} bild
 * @param {(adresse: string, beschriftung: string, naturbreite?: number) => void} beiAuswahl
 * @returns {HTMLElement}
 */
function kachel(bild, beiAuswahl) {
  const knoten = el('button.bild-kachel', {
    type: 'button',
    draggable: 'true',
    title: `${bild.beschriftung}${bild.breite ? ` · ${bild.breite}×${bild.hoehe}` : ''}`,
    // Die Naturbreite mitgeben: daraus wird die width-Angabe, die Outlook
    // braucht - ohne sie skaliert die Word-Engine das Bild auf 120 DPI hoch.
    onclick: () => beiAuswahl(bild.adresse, bild.beschriftung, bild.breite || 0),
  });

  knoten.append(
    el('div.flaeche', [
      el('img', {
        src: bild.adresse,
        alt: bild.beschriftung,
        loading: 'lazy',
        draggable: 'false',
      }),
    ]),
    el('div.name', { text: bild.beschriftung }),
  );

  // GrapesJS nimmt beim Ablegen HTML aus der Zwischenablage entgegen. Wir
  // legen fertiges MJML hinein - mit Breite und Alt-Text, damit das Bild in
  // Outlook nicht hochskaliert wird und bei blockierten Bildern etwas dasteht.
  knoten.addEventListener('dragstart', (ereignis) => {
    const breite = Math.min(bild.breite || 600, 600);
    const mjml =
      `<mj-image src="${bild.adresse}" width="${breite}px" ` +
      // Paarform statt `/>`: der HTML-Parser des Browsers kennt `mj-image`
      // nicht als inhaltloses Element und wuerde sonst alles Nachfolgende
      // zu seinem Kind machen.
      `alt="${bild.beschriftung.replace(/"/g, '')}" padding="0"></mj-image>`;
    ereignis.dataTransfer?.setData('text/html', mjml);
    ereignis.dataTransfer?.setData('text/plain', bild.adresse);
    if (ereignis.dataTransfer) ereignis.dataTransfer.effectAllowed = 'copy';
  });

  return knoten;
}
